/**
 * Agreement ID to Roxx system ID mapping
 * Maps Roxx test case management system IDs to their corresponding backend agreement IDs
 * for the feedback button feature.
 *
 * To add new mappings, simply add an entry to AGREEMENT_ID_MAPPINGS below.
 * No code deployment needed — just update this file and deploy.
 */

export const AGREEMENT_ID_MAPPINGS: Record<number, number> = {
  1233: 290, // LIDINGÖ SKOL 2024
  1229: 276, // UPPLANDS VÄSBY ÄLDRE
  1239: 277, // UPPLANDS VÄSBY LSS
  1244: 292, // LIDINGÖ ÄLDRE 2024
  1232: 274, // UPPLANDS VÄSBY SKOL
  1237: 291, // LIDINGÖ LSS 2024
  1258: 116, // BOTKYRKA (SVT)
  1236: 328, // BOTKYRKA Adults/Elderly (SVT)
  1241: 345, // Botkyrka Adults/Elderly LOKAL
  1234: 344, // Botkyrka School LOKAL
  1252: 26, // HANINGE (SVT)
  1283: 327, // HANINGE Adults/Elderly (SVT)
  1243: 329, // HANINGE LSS (SVT)
  1261: 267, // SALEM (STS)
  1259: 325, // SALEM LSS (STS)
  1277: 326, // SALEM ÄLDRE (STS)
  1280: 123, // SOLNA (SVT)
  1256: 242, // SOLNA ÄLDRE (SVT)
  1262: 257, // STOCKHOLMS STAD A3 2022 (STS)
  1230: 257, // STOCKHOLMS STAD A3 LSS 2022 (STS)
  1240: 257, // STOCKHOLMS STAD A3 ÄLDRE 2022 (STS)
  1275: 227, // STOCKHOLMS STAD B1
  1783: 383, // STOCKHOLMS STAD B2 2026
  1284: 124, // SUNDBYBERG
  1281: 237, // SUNDBYBERG LSS
  1268: 238, // SUNDBYBERG ÄLDRE
  1273: 120, // UPPLANDS BRO
  1282: 322, // UPPLANDS BRO ADULTS (SVT)
  1235: 323, // UPPLANDS BRO AFTER SCHOOL ACT (SVT)
  1255: 320, // VAXHOLM LSS (SVT)
  1292: 122, // VAXHOLM SKOL
  1276: 321, // VAXHOLM ÄLDRE
};

const NUMERIC_STRING_PATTERN = /^\d+$/;

function parseNumericAgreementId(agreementId: number | string): number | null {
  if (typeof agreementId === 'number') {
    return agreementId;
  }

  return NUMERIC_STRING_PATTERN.test(agreementId) ? parseInt(agreementId, 10) : null;
}

function findMappedRoxxId(numericId: number): number | undefined {
  // numericId is a finite number used to index a fixed numeric-keyed lookup table
  // eslint-disable-next-line security/detect-object-injection
  return AGREEMENT_ID_MAPPINGS[numericId];
}

/**
 * Maps a backend agreement ID to its Roxx system ID.
 * Returns the mapped Roxx ID if found, otherwise returns the original ID unchanged.
 * This allows safe rollout: unmapped agreements will still pass through with their original ID.
 *
 * @param agreementId Backend agreement ID from trip data
 * @returns Roxx system ID (number) or original ID (number | string) if unmapped
 */
export function mapAgreementIdToRoxx(agreementId: number | string): string | number {
  const numericId = parseNumericAgreementId(agreementId);

  if (numericId === null || !Number.isFinite(numericId)) {
    return agreementId;
  }

  return findMappedRoxxId(numericId) ?? agreementId;
}
